<template>
  <div
    class="app-autocomplete-api"
    v-handle-click-outside="handleOnOutSideClick"
    :class="{ 'input-error': !!errorMessages, 'menu-active': !!isMenuActive }"
  >
    <div class="search-container" :class="{ disabled: !!disabled }" ref="SearchContainer" @click="handleOnClickOnMenu">
      <span class="input-title">{{ label }}</span>
      <div class="selected-items">
        <div
          class="selected-item"
          v-for="(item, index) in selectedItems"
          @click="handleOnRemoveItem(item)"
          :key="`auto-selected-item-${index}`"
        >
          {{ item[itemText] }}
        </div>
        <input
          type="text"
          v-model.trim="query"
          class="search-input"
          ref="InputSearch"
          :tabindex="tabindex"
          :placeholder="`${placeholder}`"
          :disabled="disabled"
          @input="handleOnSearch"
        />
      </div>
      <FIcon size="1.4rem" name="menu-arrow" class="cursor-arrow" />
    </div>

    <div class="menu-list" :style="{ top: listTop }" v-if="!!isMenuActive" :key="`item-list-key-${itemsListKey}`">
      <div class="searching-loader" v-if="isSearching">
        <AppLoader :size="30" :width="3" color="primary" />
      </div>
      <div
        v-else
        class="list-item"
        v-for="(item, index) in list"
        @click.prevent="handleOnSelect(item)"
        :class="{ active: isItemSelected(item) }"
        :key="`list-item-${index}`"
      >
        {{ item[itemText] }}
      </div>
    </div>

    <div class="error-message" v-if="!!errorMessages">
      {{ errorMessages }}
    </div>
  </div>
</template>
<script>
/**
 * AutoCompleteApi
 * @example <AutoCompleteApi />
 */
export default {
  name: "AutoCompleteApi",

  data() {
    return {
      query: "",
      isSearching: false,
      listTop: "5rem",
      isMenuActive: false,
      list: [],
      itemsListKey: 1,
      isReady: false,
    };
  },

  props: {
    label: {
      type: String,
    },

    itemText: {
      type: String,
      default: "name",
    },

    placeholder: {
      type: String,
    },

    itemValue: {
      type: String,
      default: "id",
    },

    selectedItems: {
      type: Array,
    },

    items: {
      type: Array,
    },

    tabindex: {
      type: Number,
      default: 0,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    errorMessages: {
      type: String,
      default: "",
    },
  },

  watch: {
    items: {
      immediate: true,
      deep: true,
      handler(items) {
        this.itemsList = items;
        if (this.isReady) {
          this.handleOnOpenMenu();
        }
      },
    },
  },

  computed: {
    resetProps() {
      return { ...this.$attrs };
    },

    itemsListTop() {
      return this.$refs["SelectedItemsList"]?.clientHeight || "5rem";
    },

    itemsList: {
      get: function () {
        return this.list;
      },
      set: function (items) {
        this.list = items;
      },
    },
  },

  mounted() {
    this.setupListTop();
    this.$nextTick(() => {
      this.isReady = true;
    });
  },

  methods: {
    /***
     * Handle on search
     * @return {Promise<void>}
     */
    async handleOnSearch(event) {
      if (event.key === "Backspace") {
        return this.handleOnRemoveByKeyboard();
      }
      this.isSearching = true;
      await this.searchDebounce();
    },

    /***
     * Handle on ESC
     * @return {Promise<void>}
     */
    async handleOnRemoveByKeyboard() {
      const selected = this.selectedItems[this.selectedItems.length - 1];
      if (!selected) {
        return;
      }
      this.handleOnRemoveItem(selected);
    },

    /***
     * Handle on debounce search
     * @return {Promise<void>}
     */
    searchDebounce: _.debounce(async function () {
      this.itemsList = [];
      await this.$emit("handleOnSearch", this.query);
      await this.handleOnResetSearching();
    }, 1000),

    /***
     * Handle user clicked on the menu
     * @return {void}
     */
    handleOnClickOnMenu() {
      this.$refs.InputSearch?.focus();
      this.handleOnOpenMenu();
    },

    /***
     * Handle on select item from the list
     * @return {void}
     */
    handleOnSelect(item) {
      if (this.isItemSelected(item)) {
        return this.handleOnRemoveItem(item);
      }

      this.$emit("handleOnSelect", item);
      this.setupListTop();
    },

    /***
     * Handle remove item form the selected list
     * @return {void}
     */
    handleOnRemoveItem(item) {
      this.$emit("handleOnRemove", item);
      this.setupListTop();
    },

    /***
     * handle on change the list top position depend on the input height
     * @return {void}
     */
    setupListTop() {
      this.$nextTick(() => {
        this.listTop = `${this.$refs?.SearchContainer?.offsetHeight}px`;
      });
    },

    /***
     * Get the item is Selected
     * @return {boolean}
     */
    isItemSelected(item) {
      return this.selectedItems?.findIndex((cItem) => cItem[this.itemValue] === item[this.itemValue]) !== -1;
    },

    /***
     * Handle on open the menu list
     * @return {void}
     */
    handleOnOpenMenu() {
      this.isMenuActive = false;
      this.$nextTick(() => {
        if (!this.disabled && !!this.itemsList?.length) {
          this.isMenuActive = true;
        }
      });
    },

    /***
     * handle on close menu
     * @return {void}
     */
    handleOnCloseMenu() {
      this.$nextTick(() => {
        this.isMenuActive = false;
      });
    },

    /***
     * handle on reset searching
     * @return {void}
     */
    async handleOnResetSearching() {
      await this.$nextTick(() => {});
      this.isSearching = false;
    },

    /***
     * Handle on click out side to close menu
     * @return {void}
     */
    handleOnOutSideClick() {
      if (!!this.isMenuActive) {
        this.handleOnCloseMenu();
      }
    },
  },

  directives: {
    /**
     * directive to handle on click outside
     * @directive
     */
    "handle-click-outside": {
      bind: function (el, binding) {
        const { bubble } = binding.modifiers;
        const clickHandler = (e) => {
          if (bubble || (!el.contains(e.target) && el !== e.target)) {
            binding.value(e);
          }
        };
        el.__vueHandleClickOutside__ = clickHandler;
        document.addEventListener("click", clickHandler, true);
      },
      unbind: function (el) {
        document.removeEventListener("click", el?.__vueHandleClickOutside__);
        el.__vueHandleClickOutside__ = null;
      },
    },
  },
};
</script>

<style lang="scss" scoped src="./styles.scss" />
